"use client";

import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { AlertCircle, Loader2, RefreshCcw } from "lucide-react";
import { Suspense, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useInView } from "react-intersection-observer";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useInfinitePublicStudySets } from "@/features/studysets/api/use-get-public-studysets";
import {
  StudySetCard,
  StudySetCardsSkeleton,
} from "@/features/studysets/components/studyset-card";
import { useStudySetParams } from "@/lib/hooks/use-studyset-params";

export const PublicStudySetsInfinite = () => {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onReset={reset}
          fallbackRender={({ resetErrorBoundary }) => (
            <div className="flex flex-col h-[400px] w-full">
              <Alert variant="destructive" className="max-w-lg">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription className="flex flex-col gap-4">
                  <p>
                    There was a problem loading the study sets. Please try
                    again.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => resetErrorBoundary()}
                    className="w-fit"
                  >
                    <RefreshCcw className="mr-2 h-4 w-4" />
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            </div>
          )}
        >
          <Suspense fallback={<StudySetCardsSkeleton count={6} cols={3} />}>
            <PublicStudySetsSuspense />
          </Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
};

const PublicStudySetsSuspense = () => {
  const {
    setParams: _setParams,
    q: currentSearch,
    subject: currentSubject,
  } = useStudySetParams();

  // Setup intersection observer for infinite scroll
  const { ref: loadMoreRef, inView: isLoadingMore } = useInView({
    threshold: 0,
    rootMargin: "300px",
  });

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfinitePublicStudySets({
      search: currentSearch ?? undefined,
      subject: currentSubject ?? undefined,
      limit: 24,
    });

  // Load more when scrolling to bottom
  useEffect(() => {
    if (isLoadingMore && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [isLoadingMore, fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Flatten all pages of study sets
  const allStudySets = data?.pages.flatMap((page) => page.results) ?? [];

  if (allStudySets.length === 0) {
    return (
      <p className="text-center text-sm text-muted-foreground mt-8">
        No Study Sets found matching your criteria.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {allStudySets.map((studySet) => (
        <StudySetCard key={studySet.id} studySet={studySet} isPublic={true} />
      ))}
      {/* Loading reference element */}
      <div ref={loadMoreRef} className="col-span-full flex justify-center">
        {isFetchingNextPage && (
          <Loader2 className="text-brand-primary animate-spin size-8" />
        )}
      </div>
    </div>
  );
};
